import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, InputAdornment, Paper, TextField, Typography, Snackbar, Alert, MenuItem } from '@mui/material';
import { CalendarMonthOutlined as CalendarIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { usePagePermissions } from '../context/AuthContext';

export default function VendorFollowUp() {
  const { user, isSuperAdmin, canCreate } = usePagePermissions('vendor_followup');
  const dateInputRef = useRef(null);
  const [form, setForm] = useState({
    userCode: '',
    date: '',
    fromFirmBankCode: '',
    toVendorCode: '',
    toVendorBankCode: '',
    requirement: '',
    vendorBalance: '',
    cashReceived: '',
    adjustments: '',
  });

  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [userCodes, setUserCodes] = useState([]);
  const [allVendorData, setAllVendorData] = useState([]);
  const [vendorCodes, setVendorCodes] = useState([]);
  const [bankCodes, setBankCodes] = useState([]);
  // Derived state for dynamic bank code options
  const [toVendorBankOptions, setToVendorBankOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (form.toVendorCode && allVendorData.length > 0) {
      // Find all matches for this vendor code
      const matches = allVendorData.filter(v => v.firm_vendor_code === form.toVendorCode);
      
      // Extract unique bank codes
      const validBankCodes = [...new Set(matches.map(m => m.firm_vendor_bank_code).filter(Boolean))].sort();

      if (validBankCodes.length > 0) {
        setToVendorBankOptions(validBankCodes);
        
        // Auto-select if there is exactly one option
        if (validBankCodes.length === 1) {
          if (form.toVendorBankCode !== validBankCodes[0]) {
            setForm(prev => ({ ...prev, toVendorBankCode: validBankCodes[0] }));
          }
        } else {
          // If multiple options, ensure current selection is valid, otherwise clear it
          if (form.toVendorBankCode && !validBankCodes.includes(form.toVendorBankCode)) {
             setForm(prev => ({ ...prev, toVendorBankCode: '' }));
          }
        }
      } else {
        // Fallback if no bank codes found
        setToVendorBankOptions([]); 
        setForm(prev => ({ ...prev, toVendorBankCode: '' }));
      }
    } else {
      // If no vendor selected, clear options and clear selection
      setToVendorBankOptions([]);
      if (form.toVendorBankCode) {
        setForm(prev => ({ ...prev, toVendorBankCode: '' }));
      }
    }
  }, [form.toVendorCode, allVendorData]);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error'
  });

  useEffect(() => {
    const init = async () => {
      const isSuper = isSuperAdmin;
      setIsSuperadmin(isSuper);
      const email = user?.email || '';
      // const localCode = email ? String(email).split('@')[0] : '';
      const appCode = user?.application_code || '';
      
      if (!isSuper && appCode) {
        setForm((prev) => ({ ...prev, userCode: appCode }));
      }
      
      const token = localStorage.getItem('token') || '';

      if (isSuper) {
        try {
          const acRes = await fetch(`${API_BASE_URL}/api/user-application-codes`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const acData = await acRes.json();
          const codes = Array.isArray(acData?.items)
            ? acData.items.map((it) => it.code).filter(Boolean).sort()
            : [];
          setUserCodes(codes);
        } catch {
          setUserCodes([]);
        }
      }

      try {
        const vcRes = await fetch(`${API_BASE_URL}/api/vendor-codes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const vcData = await vcRes.json();
        let vCodes = vcData?.vendor_codes || [];
        setAllVendorData(vCodes);
        
        const userAssociations = user?.association || [];
        if (!isSuper) {
          vCodes = vCodes.filter(v => userAssociations.includes(v.firm_vendor_code));
        }

        // Filter for Vendor Codes dropdown: Only 'Vendor' type
        const vendorOnlyCodes = vCodes.filter(v => String(v.firm_vendor || '').toLowerCase() === 'vendor');
        
        // Filter for Firm Bank Codes dropdown: Only 'Firm' type
        const firmOnlyCodes = vCodes.filter(v => String(v.firm_vendor || '').toLowerCase() === 'firm');

        const uniqueVendorCodes = [...new Set(vendorOnlyCodes.map(v => v.firm_vendor_code).filter(Boolean))].sort();
        // Bank codes for 'From Firm' field should only be firm bank codes
        const uniqueBankCodes = [...new Set(firmOnlyCodes.map(v => v.firm_vendor_bank_code).filter(Boolean))].sort();
        
        setVendorCodes(uniqueVendorCodes);
        setBankCodes(uniqueBankCodes);
      } catch (e) {
        console.error('Failed to load vendor codes', e);
      }
    };
    init();
  }, [isSuperAdmin, user]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setForm({
      userCode: isSuperadmin ? '' : (user?.application_code || ''),
      date: '',
      fromFirmBankCode: '',
      toVendorCode: '',
      toVendorBankCode: '',
      requirement: '',
      vendorBalance: '',
      cashReceived: '',
      adjustments: '',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    // Basic validation
    if (!form.userCode || !form.date || !form.toVendorCode) {
      setNotification({
        open: true,
        message: 'Please fill in required fields: User Code, Date, Vendor Code',
        severity: 'error',
      });
      return;
    }
    if (!canCreate) {
      setNotification({
        open: true,
        message: 'You do not have create permission for this page',
        severity: 'error',
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/api/vendor-followup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setNotification({
          open: true,
          message: 'Vendor follow-up saved successfully!',
          severity: 'success',
        });
        handleReset();
      } else {
        setNotification({
          open: true,
          message: data.error || 'Failed to save vendor follow-up',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      setNotification({
        open: true,
        message: 'Network error. Please try again.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Layout title="Daily Vendor Follow Up">
      <Box sx={{ maxWidth: 960, mx: 'auto' }}>
        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 4 }}
        >
          Daily Vendor Follow Up
        </Typography>

        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 1,
            boxShadow: '0 18px 45px rgba(15,23,42,0.08)',
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                columnGap: 6,
                rowGap: 3,
              }}
            >
              {isSuperadmin ? (
                <TextField
                  label="Application User Code"
                  value={form.userCode}
                  onChange={handleChange('userCode')}
                  select
                  variant="standard"
                  fullWidth
                  margin="normal"
                >
                  {userCodes.map(code => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Application User Code"
                  value={form.userCode}
                  onChange={handleChange('userCode')}
                  variant="standard"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
              <TextField
                label="Date of Communication"
                value={form.date}
                onChange={handleChange('date')}
                type="date"
                variant="standard"
                fullWidth
                margin="normal"
                inputRef={dateInputRef}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input::-webkit-calendar-picker-indicator': {
                    display: 'none',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarIcon 
                        fontSize="small" 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => dateInputRef.current && dateInputRef.current.showPicker()}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="From Firm Bank Code"
                value={form.fromFirmBankCode}
                onChange={handleChange('fromFirmBankCode')}
                select
                variant="standard"
                fullWidth
                margin="normal"
              >
                {bankCodes.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Vendor Code"
                value={form.toVendorCode}
                onChange={handleChange('toVendorCode')}
                select
                variant="standard"
                fullWidth
                margin="normal"
              >
                {vendorCodes.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Vendor Bank Code"
                value={form.toVendorBankCode}
                onChange={handleChange('toVendorBankCode')}
                select
                variant="standard"
                fullWidth
                margin="normal"
              >
                {toVendorBankOptions.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Requirement"
                value={form.requirement}
                onChange={handleChange('requirement')}
                variant="standard"
                fullWidth
                margin="normal"
              />
            </Box>

            <Box
              sx={{
                mt: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                columnGap: 6,
                rowGap: 3,
              }}
            >
              <TextField
                label="Vendor Balance As Per Vendor"
                value={form.vendorBalance}
                onChange={handleChange('vendorBalance')}
                variant="standard"
                fullWidth
                margin="normal"
              />
              <TextField
                label="Cash Received"
                value={form.cashReceived}
                onChange={handleChange('cashReceived')}
                variant="standard"
                fullWidth
                margin="normal"
              />
              <TextField
                label="Adjustments"
                value={form.adjustments}
                onChange={handleChange('adjustments')}
                variant="standard"
                fullWidth
                margin="normal"
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                onClick={handleSubmit}
                disabled={!canCreate || submitting}
                sx={{
                  px: 5,
                  py: 1.2,
                  borderRadius: 0.75,
                  bgcolor: '#8e24aa',
                  '&:hover': { bgcolor: '#7b1fa2' },
                  boxShadow: '0 8px 18px rgba(126,34,206,0.35)',
                  letterSpacing: 1.5,
                }}
              >
                SUBMIT
                <Box component="span" sx={{ ml: 1.5, fontSize: 18, lineHeight: 1 }}>
                  ▶
                </Box>
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
